import {
  faAddressBook,
  faBellSlash,
  faBookOpen,
  faChartColumn,
  faBoxOpen,
  faBoxesStacked,
  faBuilding,
  faClipboardList,
  faEnvelope,
  faGaugeHigh,
  faLayerGroup,
  faLocationDot,
  faRulerCombined,
  faScrewdriverWrench,
  faShieldHalved,
  faTags,
  faTriangleExclamation,
  faTruckFast,
  faUsersGear,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { RoleName } from '../../types/api';
import { ROUTES } from '../../routes/paths';

/**
 * Catalogo unico de navegacion del panel.
 *
 * La jerarquia tiene tres niveles: sistema, grupo y entrada.
 *
 *   Sistema   una aplicacion completa dentro del mismo panel (`Almacen`)
 *   Grupo     un dominio dentro de esa aplicacion (`Catalogos`)
 *   Entrada   una pantalla concreta (`Obras`)
 *
 * El nivel de sistema existe porque el panel va a alojar mas de una
 * aplicacion. Anadir la siguiente es agregar un objeto a `NAV_SYSTEMS` con su
 * prefijo de ruta propio: ni el sidebar ni la navbar ni las migas de pan
 * necesitan cambiar.
 *
 * Cada icono se importa individualmente arriba en lugar de registrarse en una
 * libreria global, que es lo que permite al empaquetador dejar fuera los miles
 * de iconos que no se usan (ADR-019).
 *
 * El filtrado por rol es comodidad visual, nunca un control de seguridad: la
 * autorizacion real la aplica el backend en cada peticion.
 */
export interface NavEntry {
  to: string;
  label: string;
  icon: IconDefinition;
  roles: RoleName[];
  /** Rutas que todavia no existen se muestran deshabilitadas, no ocultas. */
  enabled: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  entries: NavEntry[];
}

export interface NavSystem {
  id: string;
  label: string;
  icon: IconDefinition;
  /** Prefijo comun de todas sus rutas. Resuelve a que sistema pertenece una URL. */
  basePath: string;
  roles: RoleName[];
  /**
   * Un sistema deshabilitado ocupa su sitio en el sidebar sin destino al que
   * llevar. Sirve para que el hueco de la proxima aplicacion sea visible.
   */
  enabled: boolean;
  /** Entradas sin grupo, primeras del sistema. */
  entries: NavEntry[];
  groups: NavGroup[];
}

export const NAV_SYSTEMS: NavSystem[] = [
  {
    id: 'warehouse',
    label: 'Almacen',
    icon: faWarehouse,
    basePath: ROUTES.warehouse.root,
    roles: ['ADMIN'],
    enabled: true,
    entries: [
      {
        to: ROUTES.warehouse.dashboard,
        label: 'Panel',
        icon: faGaugeHigh,
        roles: ['ADMIN'],
        enabled: true,
      },
    ],
    groups: [
      {
        id: 'warehouse-inventory',
        label: 'Inventario',
        entries: [
          {
            to: ROUTES.warehouse.products,
            label: 'Productos',
            icon: faTags,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.assets,
            label: 'Equipos',
            icon: faScrewdriverWrench,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.inventoryEntries,
            label: 'Entradas',
            icon: faClipboardList,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.stock,
            label: 'Stock por ubicación',
            icon: faBoxesStacked,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.lowStock,
            label: 'Stock bajo',
            icon: faBellSlash,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.kardex,
            label: 'Kardex',
            icon: faBookOpen,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.surpluses,
            label: 'Excedentes',
            icon: faBoxOpen,
            roles: ['ADMIN'],
            enabled: true,
          },
        ],
      },
      {
        id: 'warehouse-logistics',
        label: 'Logistica',
        entries: [
          {
            to: ROUTES.warehouse.orders,
            label: 'Órdenes',
            icon: faTruckFast,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.incidents,
            label: 'Incidentes',
            icon: faTriangleExclamation,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.emails,
            label: 'Correos',
            icon: faEnvelope,
            roles: ['ADMIN'],
            enabled: true,
          },
        ],
      },
      {
        id: 'warehouse-catalogs',
        label: 'Catalogos',
        entries: [
          {
            to: ROUTES.warehouse.projects,
            label: 'Obras',
            icon: faBuilding,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.contacts,
            label: 'Contactos',
            icon: faAddressBook,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.locations,
            label: 'Ubicaciones',
            icon: faLocationDot,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.categories,
            label: 'Categorías',
            icon: faTags,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.unitsOfMeasure,
            label: 'Unidades',
            icon: faRulerCombined,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.suppliers,
            label: 'Proveedores',
            icon: faBuilding,
            roles: ['ADMIN'],
            enabled: true,
          },
        ],
      },
      {
        id: 'warehouse-administration',
        label: 'Administracion',
        entries: [
          {
            to: ROUTES.warehouse.users,
            label: 'Usuarios',
            icon: faUsersGear,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.projectReport,
            label: 'Material por obra',
            icon: faChartColumn,
            roles: ['ADMIN'],
            enabled: true,
          },
          {
            to: ROUTES.warehouse.auditLogs,
            label: 'Auditoria',
            icon: faShieldHalved,
            roles: ['ADMIN'],
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'driver',
    label: 'Chofer',
    icon: faTruckFast,
    basePath: ROUTES.driver.root,
    roles: ['DRIVER'],
    enabled: true,
    entries: [
      {
        to: ROUTES.driver.orders,
        label: 'Mis órdenes',
        icon: faTruckFast,
        roles: ['DRIVER'],
        enabled: true,
      },
    ],
    groups: [],
  },
  {
    // Plantilla de la proxima aplicacion. Se deja visible y deshabilitada para
    // que el hueco exista en el diseno antes que en el codigo: cuando llegue,
    // se sustituye por un sistema con `enabled: true`, su `basePath` y sus
    // grupos, y ni el sidebar ni la navbar necesitan cambiar.
    id: 'next-app',
    label: 'Próxima aplicación',
    icon: faLayerGroup,
    basePath: '/next-app',
    roles: ['ADMIN'],
    enabled: false,
    entries: [],
    groups: [],
  },
];

/** Titulo legible de cada ruta. Alimenta las migas de pan y el `document.title`. */
export const ROUTE_TITLES: Record<string, string> = {
  [ROUTES.warehouse.dashboard]: 'Panel',
  [ROUTES.warehouse.products]: 'Productos',
  [ROUTES.warehouse.productAttributes]: 'Atributos de producto',
  [ROUTES.warehouse.assets]: 'Equipos',
  [ROUTES.warehouse.inventoryEntries]: 'Entradas de inventario',
  [ROUTES.warehouse.stock]: 'Stock por ubicación',
  [ROUTES.warehouse.lowStock]: 'Stock bajo',
  [ROUTES.warehouse.kardex]: 'Kardex',
  [ROUTES.warehouse.surpluses]: 'Excedentes',
  [ROUTES.warehouse.orders]: 'Órdenes de traslado',
  [ROUTES.warehouse.incidents]: 'Incidentes',
  [ROUTES.warehouse.emails]: 'Correos de comprobantes',
  [ROUTES.warehouse.projectReport]: 'Material por obra',
  [ROUTES.warehouse.orderNew]: 'Nueva orden',
  [ROUTES.warehouse.projects]: 'Obras',
  [ROUTES.warehouse.contacts]: 'Contactos',
  [ROUTES.warehouse.locations]: 'Ubicaciones',
  [ROUTES.warehouse.categories]: 'Categorías',
  [ROUTES.warehouse.unitsOfMeasure]: 'Unidades de medida',
  [ROUTES.warehouse.suppliers]: 'Proveedores',
  [ROUTES.warehouse.users]: 'Usuarios',
  [ROUTES.warehouse.auditLogs]: 'Auditoria',
  [ROUTES.driver.orders]: 'Mis órdenes',
  [ROUTES.forbidden]: 'Sin permiso',
};

/** Sistema al que pertenece una ruta, por su prefijo. */
export function systemFor(pathname: string): NavSystem | null {
  return (
    NAV_SYSTEMS.find(
      (system) => pathname === system.basePath || pathname.startsWith(`${system.basePath}/`),
    ) ?? null
  );
}

/** Grupo al que pertenece una ruta, para abrirlo solo y para las migas. */
export function groupFor(pathname: string): NavGroup | null {
  for (const system of NAV_SYSTEMS) {
    for (const group of system.groups) {
      if (group.entries.some((entry) => pathname.startsWith(entry.to))) {
        return group;
      }
    }
  }
  return null;
}

export function titleFor(pathname: string): string {
  const exact = ROUTE_TITLES[pathname];
  if (exact !== undefined) {
    return exact;
  }

  // Una ruta de detalle (`/warehouse/products/12`) hereda el titulo de su
  // listado. El nombre del recurso concreto lo pone la propia pantalla, que es
  // la unica que lo ha cargado.
  const parent = pathname.slice(0, pathname.lastIndexOf('/'));
  return ROUTE_TITLES[parent] ?? 'Inventario y Logistica';
}

export function visibleEntries(entries: NavEntry[], role: RoleName): NavEntry[] {
  return entries.filter((entry) => entry.roles.includes(role));
}

/** Sistemas que el rol puede ver. Comodidad visual, nunca control de acceso. */
export function visibleSystems(role: RoleName): NavSystem[] {
  return NAV_SYSTEMS.filter((system) => system.roles.includes(role));
}
